import type * as Collapsible from '@radix-ui/react-collapsible';
import * as React from 'react';

interface CollapsibleDrawerContextType extends Omit<Collapsible.CollapsibleProps, 'title'> {
  side: 'left' | 'right';
  title: string;
  contentWidth?: string;
  /**
   * Resize state supplied by the root when `isResizable` is set. `undefined`
   * for non-resizable drawers, which keep their fixed grid-column widths.
   */
  resize?: {
    width: number;
    isResizing: boolean;
    separatorProps: React.ComponentPropsWithoutRef<'div'>;
    /**
     * Whether the panel is wider than its docked baseline (`minWidth`). The
     * grid column stays pinned at `minWidth` regardless, so this is what
     * decides whether the panel is still in-flow (`false`, "docked") or has
     * been lifted out of flow to overlay the page content (`true`,
     * "floating").
     */
    isFloating: boolean;
  };
}

const CollapsibleDrawerContext = React.createContext<CollapsibleDrawerContextType | undefined>(undefined);

interface CollapsibleDrawerProviderProps {
  value: CollapsibleDrawerContextType;
  children: React.ReactNode;
}

function CollapsibleDrawerProvider(props: CollapsibleDrawerProviderProps) {
  const { children, value } = props;

  return <CollapsibleDrawerContext.Provider value={value}>{children}</CollapsibleDrawerContext.Provider>;
}

function useCollapsibleDrawer() {
  const context = React.useContext(CollapsibleDrawerContext);

  if (context === undefined) {
    throw new Error('useCollapsibleDrawer must be used within a CollapsibleDrawerProvider');
  }

  return context;
}

export { CollapsibleDrawerProvider, useCollapsibleDrawer };
